import React, { useCallback, useEffect, useState } from 'react';
import * as mentorApi from '../../api/mentorApi';
import { useMentorQuery } from '../../api/mentorQuery';
import { enrichCohortCards, enrichSchoolSummaries } from '../../utils/year2Ui';
import Year2SchoolDashboard from './Year2SchoolDashboard';
import Year2PortfolioCohortCards from './Year2PortfolioCohortCards';
import Year2PortfolioDetail from './Year2PortfolioDetail';

export default function Year2PortfolioFlow({
  user,
  liveSchools = [],
  preloadedSchools = null,
  preloadedCohortsBySchool = null,
  bootstrapLoading = false,
  bootstrapError = '',
  onRefresh,
  onError,
  onSuccess,
}) {
  const [view, setView] = useState('schools');
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [cohortCards, setCohortCards] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);

  const usePreloaded = Array.isArray(preloadedSchools);
  const queryEnabled = !usePreloaded && !bootstrapLoading && !bootstrapError;

  const { data: queriedSchools, isLoading: schoolsLoading, error: schoolsError, refetch: refetchSchools } = useMentorQuery(
    ['year2', 'portfolio', 'schools'],
    () => mentorApi.getYear2SquadSetupSchools(),
    { enabled: queryEnabled, caller: 'Year2PortfolioFlow:schools' }
  );

  const { data: queriedCohorts, isLoading: cohortsLoading, refetch: refetchCohorts } = useMentorQuery(
    ['year2', 'portfolio', 'cohorts', selectedSchoolId],
    () => mentorApi.getYear2SquadSetupCohorts(selectedSchoolId),
    { enabled: Boolean(selectedSchoolId) && view === 'cohorts', caller: 'Year2PortfolioFlow:cohorts' }
  );

  const applySchools = useCallback((rows = []) => {
    setSchools(enrichSchoolSummaries(rows, liveSchools));
  }, [liveSchools]);

  const applyCohorts = useCallback((rows = []) => {
    setCohortCards(enrichCohortCards(rows, liveSchools));
  }, [liveSchools]);

  useEffect(() => {
    if (usePreloaded) {
      applySchools(preloadedSchools);
    } else if (queriedSchools) {
      applySchools(queriedSchools);
    }
  }, [usePreloaded, preloadedSchools, queriedSchools, applySchools]);

  useEffect(() => {
    if (schoolsError) {
      onError?.(schoolsError?.message || 'Failed to load Year 2 schools');
      setSchools([]);
    }
  }, [schoolsError, onError]);

  useEffect(() => {
    if (queriedCohorts) applyCohorts(queriedCohorts);
  }, [queriedCohorts, applyCohorts]);

  const loadSchools = useCallback(() => {
    if (usePreloaded) {
      applySchools(preloadedSchools);
    } else {
      refetchSchools();
    }
  }, [usePreloaded, preloadedSchools, applySchools, refetchSchools]);

  const loadCohorts = useCallback(async (schoolId) => {
    const cachedRows = preloadedCohortsBySchool?.[String(schoolId)];
    if (Array.isArray(cachedRows)) {
      applyCohorts(cachedRows);
      return;
    }
    setSelectedSchoolId(schoolId);
    refetchCohorts();
  }, [preloadedCohortsBySchool, applyCohorts, refetchCohorts]);

  useEffect(() => {
    if (!selectedSchool || !Array.isArray(preloadedSchools)) return;
    const selId = selectedSchool.school_id ?? selectedSchool.schoolId ?? selectedSchool.id;
    const refreshed = preloadedSchools.find(
      (s) => String(s.school_id ?? s.schoolId ?? s.id) === String(selId)
    );
    if (refreshed) {
      setSelectedSchool(refreshed);
    }
  }, [preloadedSchools, selectedSchool]);

  const handleSelectSchool = async (school) => {
    setSelectedSchool(school);
    setView('cohorts');
    const schoolId = school.school_id ?? school.schoolId ?? school.id;
    await loadCohorts(String(schoolId));
  };

  const handleSelectCohort = (cohort) => {
    setSelectedCohort(cohort);
    setView('detail');
  };

  const handleBackToSchools = () => {
    setView('schools');
    setSelectedSchool(null);
    setSelectedSchoolId(null);
    setCohortCards([]);
    setSelectedCohort(null);
    loadSchools();
  };

  const handleBackToCohorts = () => {
    setView('cohorts');
    setSelectedCohort(null);
    if (selectedSchool) {
      const schoolId = selectedSchool.school_id ?? selectedSchool.schoolId ?? selectedSchool.id;
      void loadCohorts(String(schoolId));
    }
  };

  // ── Render views ──────────────────────────────────────────────

  if (bootstrapLoading && !Array.isArray(preloadedSchools)) {
    return (
      <div className="placeholder-card animate-fade-in">
        <p>Loading Year 2 discovery…</p>
      </div>
    );
  }

  if (bootstrapError && !Array.isArray(preloadedSchools)) {
    return (
      <div className="placeholder-card animate-fade-in">
        <h2>Could not load Year 2 discovery</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: 1.5 }}>{bootstrapError}</p>
        <button
          type="button"
          className="btn-form-toggle-outlined"
          style={{ marginTop: '12px' }}
          onClick={() => { void (onRefresh ? onRefresh().catch(() => {}) : undefined); }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (view === 'detail' && selectedCohort) {
    const cohortId = selectedCohort.cohort_id ?? selectedCohort.id;
    const cohortName = selectedCohort.cohort_name ?? selectedCohort.name;
    const schoolName = selectedSchool?.school_name ?? '';

    return (
      <Year2PortfolioDetail
        cohortId={String(cohortId)}
        cohortName={cohortName}
        schoolName={schoolName}
        isPA={user?.role === 'program_administrator'}
        onBack={handleBackToCohorts}
        onError={onError}
        onSuccess={onSuccess}
      />
    );
  }

  if (view === 'cohorts' && selectedSchool) {
    return (
      <Year2PortfolioCohortCards
        schoolName={selectedSchool.school_name}
        cohorts={cohortCards}
        loading={cohortsLoading}
        onBack={handleBackToSchools}
        onSelectCohort={handleSelectCohort}
      />
    );
  }

  return (
    <Year2SchoolDashboard
      schools={schools}
      loading={schoolsLoading}
      onSelectSchool={handleSelectSchool}
    />
  );
}
